import { useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { IOnePlusManyInstanceConfig } from 'common/contracts';
import {
  isOnePlusManyInstanceId,
  OnePlusManyInstanceId
} from 'constants/bodyBlocks';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';

import { buyOneGetManyDesigns } from 'components/TemplatesStudio/DesignsMetadata/buyOneGetMany.metadata';
import { BuyOneGetManyDesignsMetadata } from 'components/TemplatesStudio/DesignsMetadata/types';
import { IOnePlusManyFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks/multiInstanceBlocks.types';

import { DesignSidebarItem } from '../Components/DesignSidebarItem';

import { designMatchesRewardsLayout } from './BuyOneGetMany.utils';

export const getBuyOneGetManyDesignsByFilter = ({
  desktopLayout,
  themes,
  rewardsLayout
}: Pick<
  IOnePlusManyInstanceConfig,
  'desktopLayout' | 'themes' | 'rewardsLayout'
>): BuyOneGetManyDesignsMetadata[] => {
  return Object.values(buyOneGetManyDesigns).filter((currentDesign) => {
    const matchesLayout = currentDesign.layout.includes(desktopLayout);
    const matchesTheme =
      !themes || themes.length === 0 || themes.includes(currentDesign.theme);
    return (
      matchesLayout &&
      matchesTheme &&
      designMatchesRewardsLayout(currentDesign, rewardsLayout)
    );
  });
};

export const BuyOneGetManyDesignsSecondarySidebar: React.FC = () => {
  const { control, setValue } = useFormContext();
  const { openSidebarMultiInstanceBlockId } = useTemplateStudioState();

  let blockId: OnePlusManyInstanceId | undefined;
  if (
    openSidebarMultiInstanceBlockId !== undefined &&
    isOnePlusManyInstanceId(openSidebarMultiInstanceBlockId)
  ) {
    blockId = openSidebarMultiInstanceBlockId;
  }

  const storeBlocks = useWatch({ control, name: 'storeBlocks' }) as
    | IOnePlusManyFormStoreBlock[]
    | undefined;
  const instanceIndex = (storeBlocks || []).findIndex(
    (block) => block.id === blockId || block.instanceKey === blockId
  );

  let activeEntry: IOnePlusManyInstanceConfig | undefined;
  if (instanceIndex !== -1) {
    activeEntry = storeBlocks?.[instanceIndex] as IOnePlusManyInstanceConfig;
  }

  const designs = useMemo(() => {
    if (!activeEntry) return [];
    return getBuyOneGetManyDesignsByFilter({
      desktopLayout: activeEntry.desktopLayout,
      themes: activeEntry.themes,
      rewardsLayout: activeEntry.rewardsLayout
    });
  }, [activeEntry]);

  if (!blockId || !activeEntry) return null;

  const currentRewardsLayout = activeEntry.rewardsLayout;
  const blockDesignIdPath = `storeBlocks.${instanceIndex}.selectedDesignId`;
  const rewardsLayoutPath = `storeBlocks.${instanceIndex}.rewardsLayout`;

  const syncRewardsLayoutToDesign = (
    design: BuyOneGetManyDesignsMetadata
  ): void => {
    if (design.rewardsLayouts.includes(currentRewardsLayout)) return;
    setValue(rewardsLayoutPath, design.rewardsLayouts[0], {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  return (
    <>
      {designs.map((design) => (
        <DesignSidebarItem
          key={design.id}
          {...design}
          blockDesignIdPath={blockDesignIdPath}
          onDesignSelect={() => syncRewardsLayoutToDesign(design)}
        />
      ))}
    </>
  );
};
